import React, { useEffect, useState } from 'react'

import { Chip, EmptyState, Icon } from '@clientbase/clientbase-library'
import { Box, Tooltip, Typography } from '@mui/material'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { DisputesService } from 'services/disputes/disputesService'
import {
  DISPUTE_STATUS_LABEL_SHORT,
  getDisputeChipSx
} from 'services/disputes/disputeStatus'
import { DisputeItem } from 'services/disputes/types'

import { ButtonRounded } from 'components/button'
import { Table } from 'components/Table/Table'
import { HeadCell } from 'components/Table/Table.interface'
import { TableNameWrapper, TableNickname } from 'components/Table/Table.styles'
import { TableSkeleton } from 'components/Table/TableSkeleton'

import { formatMoney } from 'utils'

import { theme } from 'styles/theme'

import * as S from '../../ModalBalance.styles'
import { DisputeDetailDrawer } from './DisputeDetailDrawer'

const STATUS_LEGEND: { label: string; description: string }[] = [
  {
    label: 'Aguardando envio',
    description: 'Anexe os documentos e envie a defesa dentro do prazo.'
  },
  {
    label: 'Gerando defesa',
    description: 'Montando o PDF de defesa com os documentos enviados.'
  },
  {
    label: 'Defesa enviada',
    description: 'A defesa foi enviada e está em análise.'
  },
  { label: 'Ganha', description: 'A defesa foi aceita e o valor foi mantido.' },
  {
    label: 'Chargeback',
    description: 'O estorno foi confirmado e o valor devolvido ao titular.'
  },
  {
    label: 'Falha no envio',
    description:
      'A defesa foi enviada, mas o arquivo não pôde ser gerado a tempo e não chegou à operadora.'
  },
  {
    label: 'Expirada',
    description: 'O prazo para enviar a defesa terminou.'
  }
]

const StatusHeader = () => (
  <Tooltip
    arrow
    enterTouchDelay={0}
    title={
      <Box>
        {STATUS_LEGEND.map((item) => (
          <p key={item.label}>
            <b>{item.label}: </b>
            {item.description}
          </p>
        ))}
      </Box>
    }
  >
    <Box display="flex" alignItems="center" gap="4px" width="fit-content">
      Status
      <Icon
        icon="error"
        sx={{ color: theme.palette.neutral[500], fontSize: '1rem' }}
      />
    </Box>
  </Tooltip>
)

const HEADER: HeadCell[] = [
  { id: 'customer', label: 'Cliente' },
  { id: 'amount', label: 'Valor' },
  { id: 'date', label: 'Data da contestação' },
  { id: 'deadline', label: 'Prazo para defesa' },
  { id: 'status', label: <StatusHeader /> },
  { id: 'actions', label: '' }
]

interface Props {
  active: boolean
  initialDisputeUuid?: string | null
  onOpenBillingDetail?: (billingUuid: string) => void
}

export const DisputesList = ({
  active,
  initialDisputeUuid,
  onOpenBillingDetail
}: Props) => {
  const [loading, setLoading] = useState(false)
  const [records, setRecords] = useState<DisputeItem[]>([])
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)
  const [totalCount, setTotalCount] = useState(0)
  const [selectedUuid, setSelectedUuid] = useState<string | null>(null)

  useEffect(() => {
    if (
      initialDisputeUuid &&
      records.some((r) => r.uuid === initialDisputeUuid)
    ) {
      setSelectedUuid(initialDisputeUuid)
    }
  }, [initialDisputeUuid, records])

  useEffect(() => {
    if (!active) return

    const fetch = async () => {
      setLoading(true)
      try {
        const data = await DisputesService.list(page + 1, rowsPerPage)
        setRecords(data.records)
        setTotalCount(data.meta.total_count)
      } catch {
        // error handled silently
      } finally {
        setLoading(false)
      }
    }

    fetch()
  }, [active, page, rowsPerPage])

  const handleChangePage = (
    _: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number
  ) => setPage(newPage)

  const handleChangeRowsPerPage = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(Number(e.target.value || 25))
    setPage(0)
  }

  if (loading) {
    return <TableSkeleton />
  }

  if (!loading && !records.length) {
    return (
      <S.EmptyStateWrapper>
        <Box maxWidth="600px" width="100%">
          <EmptyState
            title="Nenhuma contestação encontrada"
            message="Pagamentos contestados (em disputa) e chargebacks serão exibidos aqui. Acompanhe o status e a evolução de cada contestação em um só lugar."
          />
        </Box>
      </S.EmptyStateWrapper>
    )
  }

  const rows = records.map((item) => {
    const formattedDate = item.disputed_at
      ? format(new Date(item.disputed_at), 'dd/MM/yyyy', { locale: ptBR })
      : ''
    // O prazo só conta enquanto a defesa não foi enviada; depois disso (ou vencido) é '—'.
    const awaitingDefense = item.status === 'awaiting_documents'
    const isUrgent = (item.days_remaining ?? 99) <= 1 && awaitingDefense
    const billingUuid = item.billing?.uuid ?? null

    return {
      uuid: item.uuid,
      clickable: true,
      customer: (
        <TableNameWrapper>
          <Typography variant="smLight">{item.customer?.name ?? ''}</Typography>
          {item.customer?.nickname && (
            <TableNickname>{item.customer.nickname}</TableNickname>
          )}
        </TableNameWrapper>
      ),
      amount: (
        <Typography variant="smLight">
          {item.billing ? formatMoney(Number(item.billing.amount_billed)) : ''}
        </Typography>
      ),
      date: (
        <Typography variant="smLight" color="text.secondary">
          {formattedDate}
        </Typography>
      ),
      deadline: !item.dispute_deadline_at ? null : !awaitingDefense ? (
        <Typography variant="smLight" color="text.secondary">
          —
        </Typography>
      ) : (
        <Typography
          variant="smLight"
          color={isUrgent ? theme.palette.error[300] : 'text.secondary'}
          fontWeight={isUrgent ? 600 : undefined}
        >
          {item.days_remaining === 0
            ? 'Hoje'
            : item.days_remaining === 1
            ? '1 dia'
            : item.days_remaining !== null
            ? `${item.days_remaining} dias`
            : ''}
        </Typography>
      ),
      status: (
        <Chip
          variant="payment"
          label={DISPUTE_STATUS_LABEL_SHORT[item.status]}
          sx={getDisputeChipSx(item.status)}
        />
      ),
      actions: (
        <Box display="flex" justifyContent="flex-end" alignItems="center">
          {billingUuid && (
            <Tooltip
              enterTouchDelay={0}
              arrow
              placement="top"
              PopperProps={{ disablePortal: true }}
              title="Visualizar fatura"
            >
              <Box>
                <ButtonRounded
                  onClick={(e) => {
                    e.stopPropagation()
                    onOpenBillingDetail?.(billingUuid)
                  }}
                  variant="transparent"
                >
                  <Icon
                    icon="receipt"
                    sx={{ color: theme.palette.neutral[200] }}
                  />
                </ButtonRounded>
              </Box>
            </Tooltip>
          )}
          <Tooltip
            enterTouchDelay={0}
            arrow
            placement="top"
            PopperProps={{ disablePortal: true }}
            title="Ver detalhes"
          >
            <Box>
              <ButtonRounded
                onClick={(e) => {
                  e.stopPropagation()
                  setSelectedUuid(item.uuid)
                }}
                variant="transparent"
              >
                <Icon
                  icon="settings"
                  sx={{ color: theme.palette.neutral[200] }}
                />
              </ButtonRounded>
            </Box>
          </Tooltip>
        </Box>
      )
    }
  })

  return (
    <>
      <S.TableWrapper>
        <Table
          mobileCards
          header={HEADER}
          rows={rows}
          disableFilter
          select={{ useSelect: false }}
          hoverInRow
          hideLastRowSeparator
          rowAction={(uuid) => setSelectedUuid(uuid)}
          page={page}
          rowsPerPage={rowsPerPage}
          totalCount={totalCount}
          handleChangePage={handleChangePage}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
        />
      </S.TableWrapper>

      <DisputeDetailDrawer
        uuid={selectedUuid}
        onClose={() => setSelectedUuid(null)}
        onOpenBillingDetail={onOpenBillingDetail}
      />
    </>
  )
}
